import React, { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  ImageIcon,
  Loader2,
  ZoomIn,
  ZoomOut,
  RotateCcw,
} from "lucide-react";
import {
  TransformWrapper,
  TransformComponent,
  useControls,
} from "react-zoom-pan-pinch";
import SimpleToggleButton from "../../../common/SimpleToggleButton/SimpleToggleButton";
import { CARD_CLASS } from "../../../common/CertificateOnboarding/utils/styles";
import {
  getPreviewFormatLabel,
  isBrowserPreviewableUrl,
} from "../../../common/CertificateOnboarding/utils/preview";
import {
  isSvgTemplateUrl,
  processSvgTemplate,
} from "../../../common/CertificateOnboarding/utils/svgTemplatePreview";
import { variableConfigHasCoordinates } from "../../../common/CertificateOnboarding/utils/variables";

function PreviewZoomControls({ t }) {
  const { zoomIn, zoomOut, resetTransform } = useControls();

  return (
    <div className="flex items-center gap-1 rounded-lg border border-cfCardBorder bg-cfSecondary p-1 shadow-sm">
      <button
        type="button"
        onClick={() => zoomOut()}
        className="p-1.5 rounded-md text-cfGlobalText hover:bg-cfSecondary hover:text-cfHeadingText transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary"
        aria-label={t("certificateOnboarding:zoom_out")}
      >
        <ZoomOut className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => resetTransform()}
        className="p-1.5 rounded-md text-cfGlobalText hover:bg-cfSecondary hover:text-cfHeadingText transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary"
        aria-label={t("certificateOnboarding:reset_zoom")}
      >
        <RotateCcw className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => zoomIn()}
        className="p-1.5 rounded-md text-cfGlobalText hover:bg-cfSecondary hover:text-cfHeadingText transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary"
        aria-label={t("certificateOnboarding:zoom_in")}
      >
        <ZoomIn className="w-4 h-4" />
      </button>
    </div>
  );
}

function TemplateSvgPreview({
  templateSvgUrl,
  previewSvgUrl,
  variableConfig = {},
  enableFillDefaultsPreview = false,
  t,
}) {
  const [showPreview, setShowPreview] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [fillWithDefaults, setFillWithDefaults] = useState(false);
  const [templateSvgText, setTemplateSvgText] = useState(null);
  const [templateSvgLoading, setTemplateSvgLoading] = useState(false);
  const [templateSvgError, setTemplateSvgError] = useState(false);
  const [substitutedPreviewUrl, setSubstitutedPreviewUrl] = useState(null);
  const [substituteLoading, setSubstituteLoading] = useState(false);
  const [substituteError, setSubstituteError] = useState(false);
  const [unmatchedPlaceholders, setUnmatchedPlaceholders] = useState([]);
  const [replacedCount, setReplacedCount] = useState(0);
  const blobUrlRef = useRef(null);

  const rawDisplayUrl = templateSvgUrl || previewSvgUrl;
  const canSubstituteTemplate = isSvgTemplateUrl(templateSvgUrl);
  const hasCoordinates = variableConfigHasCoordinates(variableConfig);
  const shouldProcessSvg =
    enableFillDefaultsPreview &&
    canSubstituteTemplate &&
    (fillWithDefaults || hasCoordinates);
  const displayUrl =
    shouldProcessSvg && substitutedPreviewUrl
      ? substitutedPreviewUrl
      : rawDisplayUrl;
  const formatLabel = getPreviewFormatLabel(rawDisplayUrl);
  const canPreview = displayUrl && isBrowserPreviewableUrl(displayUrl);

  const revokeBlobUrl = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  };

  useEffect(() => {
    setLoadError(false);
  }, [displayUrl]);

  useEffect(() => {
    let cancelled = false;

    if (!canSubstituteTemplate || !templateSvgUrl) {
      setTemplateSvgText(null);
      setTemplateSvgLoading(false);
      setTemplateSvgError(false);
      return undefined;
    }

    setTemplateSvgLoading(true);
    setTemplateSvgError(false);

    fetch(templateSvgUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch SVG template (${response.status})`);
        }
        return response.text();
      })
      .then((svgText) => {
        if (!cancelled) {
          setTemplateSvgText(svgText);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTemplateSvgText(null);
          setTemplateSvgError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setTemplateSvgLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canSubstituteTemplate, templateSvgUrl]);

  useEffect(() => {
    if (!shouldProcessSvg) {
      revokeBlobUrl();
      setSubstitutedPreviewUrl(null);
      setUnmatchedPlaceholders([]);
      setReplacedCount(0);
      setSubstituteError(false);
      setSubstituteLoading(false);
      return undefined;
    }

    if (templateSvgLoading) {
      setSubstituteLoading(true);
      return undefined;
    }

    if (templateSvgError || !templateSvgText) {
      setSubstituteError(true);
      setSubstituteLoading(false);
      setSubstitutedPreviewUrl(null);
      return undefined;
    }

    setSubstituteLoading(true);
    setSubstituteError(false);

    try {
      const { svg, unmatched, replacedCount: count } = processSvgTemplate(
        templateSvgText,
        variableConfig,
        { fillDefaults: fillWithDefaults },
      );

      revokeBlobUrl();
      const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
      const nextUrl = URL.createObjectURL(blob);
      blobUrlRef.current = nextUrl;
      setSubstitutedPreviewUrl(nextUrl);
      setUnmatchedPlaceholders(unmatched);
      setReplacedCount(count);
      setSubstituteLoading(false);
    } catch {
      setSubstituteError(true);
      setSubstitutedPreviewUrl(null);
      setUnmatchedPlaceholders([]);
      setReplacedCount(0);
      setSubstituteLoading(false);
    }

    return undefined;
  }, [
    shouldProcessSvg,
    fillWithDefaults,
    variableConfig,
    templateSvgText,
    templateSvgLoading,
    templateSvgError,
  ]);

  useEffect(() => {
    if (!enableFillDefaultsPreview) {
      setFillWithDefaults(false);
    }
  }, [enableFillDefaultsPreview]);

  useEffect(() => () => revokeBlobUrl(), []);

  const handleUrlChange = () => {
    setLoadError(false);
  };

  const showSubstitutePanel =
    enableFillDefaultsPreview && canSubstituteTemplate;
  const isPreviewBusy = substituteLoading && shouldProcessSvg;
  const showPositionPreviewStatus =
    shouldProcessSvg && hasCoordinates && !fillWithDefaults;

  return (
    <div
      id="cert-onboarding-template-preview"
      className={`${CARD_CLASS} lg:sticky lg:top-24 z-0`}
    >
      <div className="px-2 py-3 mx-2 sm:px-5 border-b border-cfCardBorder">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-cfHeadingText">
              {t("certificateOnboarding:preview_section_title")}
            </h2>
            {formatLabel && (
              <p className="text-xs text-cfGlobalText mt-0.5">
                {t("certificateOnboarding:preview_format_supported", {
                  format: formatLabel,
                })}
              </p>
            )}
          </div>
          <button
            type="button"
            className="lg:hidden shrink-0 text-sm text-cfPrimary font-medium hover:text-cfPrimaryDark transition-colors"
            onClick={() => setShowPreview((prev) => !prev)}
          >
            {showPreview
              ? t("certificateOnboarding:hide_preview")
              : t("certificateOnboarding:show_preview")}
          </button>
        </div>

        {showPositionPreviewStatus && (
          <div className="mt-3 rounded-lg border border-cfPrimaryBorder bg-cfPrimaryMuted/40 px-3 py-2.5">
            <p className="text-xs text-cfPrimary font-medium leading-relaxed">
              {t("certificateOnboarding:svg_position_preview_active")}
            </p>
          </div>
        )}

        {showSubstitutePanel && (
          <div className="mt-4 rounded-xl border border-cfCardBorder bg-cfPrimaryMuted/30 px-3.5 py-3">
            <div className="flex items-start gap-3">
              <SimpleToggleButton
                checked={fillWithDefaults}
                onChange={setFillWithDefaults}
                disabled={substituteLoading}
                testId="cert-onboarding-fill-defaults-toggle"
                ariaLabel={t("certificateOnboarding:svg_fill_defaults_title")}
              />
              <div className="min-w-0 flex-1">
                <label
                  htmlFor="cert-onboarding-fill-defaults-toggle"
                  className="text-sm font-medium text-cfHeadingText cursor-pointer"
                >
                  {t("certificateOnboarding:svg_fill_defaults_title")}
                </label>
                <p className="text-xs text-cfGlobalText mt-1 leading-relaxed">
                  {t("certificateOnboarding:svg_fill_defaults_hint")}
                </p>
                {fillWithDefaults && !substituteLoading && !substituteError && (
                  <p className="text-xs text-cfPrimary mt-2 font-medium">
                    {t("certificateOnboarding:svg_fill_defaults_replaced", {
                      count: replacedCount,
                    })}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={`p-2 ${showPreview ? "block" : "hidden lg:block"}`}>
        {shouldProcessSvg &&
          fillWithDefaults &&
          unmatchedPlaceholders.length > 0 && (
          <div className="mx-2 mb-3 flex items-start gap-2 rounded-lg border border-cfPrimaryBorder bg-cfPrimaryMuted px-3 py-2.5">
            <AlertTriangle
              className="w-4 h-4 shrink-0 text-cfPrimary mt-0.5"
              aria-hidden
            />
            <p className="text-xs text-cfHeadingText leading-relaxed">
              {t("certificateOnboarding:svg_fill_defaults_unmatched", {
                placeholders: unmatchedPlaceholders.join(", "),
              })}
            </p>
          </div>
        )}

        {isPreviewBusy ? (
          <div className="bg-cfSecondary rounded-xl border border-dashed border-cfCardBorder p-8 text-center min-h-[260px] flex flex-col items-center justify-center gap-3">
            <Loader2
              className="w-8 h-8 text-cfPrimary animate-spin"
              aria-hidden
            />
            <p className="text-sm text-cfGlobalText">
              {t("certificateOnboarding:svg_fill_defaults_loading")}
            </p>
          </div>
        ) : canPreview &&
          !loadError &&
          !(shouldProcessSvg && substituteError) ? (
          <TransformWrapper
            initialScale={1}
            minScale={0.5}
            maxScale={3}
            centerOnInit
            wheel={{ step: 0.08 }}
            doubleClick={{ mode: "reset" }}
          >
            <div className="flex justify-end mb-3 px-1">
              <PreviewZoomControls t={t} />
            </div>
            <TransformComponent
              wrapperClass="!w-full"
              contentClass="!w-full flex justify-center"
            >
              <div className="bg-cfSecondary rounded-xl border border-cfCardBorder/80 p-4 w-full flex justify-center items-center min-h-[260px] max-h-[460px] overflow-hidden">
                <img
                  key={displayUrl}
                  src={displayUrl}
                  alt={t("certificateOnboarding:template_preview_alt")}
                  onLoad={handleUrlChange}
                  onError={() => setLoadError(true)}
                  className="max-w-full max-h-[420px] object-contain select-none"
                  draggable={false}
                />
              </div>
            </TransformComponent>
          </TransformWrapper>
        ) : (
          <div className="bg-cfSecondary rounded-xl border border-dashed border-cfCardBorder p-8 text-center min-h-[220px] flex flex-col items-center justify-center gap-3">
            <div className="w-12 h-12 rounded-full bg-cfSecondary border border-cfCardBorder flex items-center justify-center">
              <ImageIcon className="w-6 h-6 text-cfGlobalText" />
            </div>
            <p className="text-sm text-cfGlobalText max-w-xs">
              {substituteError
                ? t("certificateOnboarding:svg_fill_defaults_failed")
                : loadError
                  ? t("certificateOnboarding:preview_load_failed")
                  : t("certificateOnboarding:preview_unavailable")}
            </p>
            {(loadError || substituteError) && rawDisplayUrl && (
              <p className="text-xs text-cfGlobalText/80 break-all max-w-full px-2">
                {rawDisplayUrl}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default TemplateSvgPreview;
